/**
 * VoicePicker — Live API voice selector for the sesli konuşma feature.
 *
 * Curated list of feminine / soft voices that suit the AI_Companion
 * persona. The full Live API exposes ~30 voices but most are not a
 * good fit, so we expose only the ten that sound natural for this
 * product. Default is `'Leda'` (youthful, energetic feminine).
 */

import type { ChangeEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { VoiceName } from '../../domain/types';
import { usePreferencesStore } from '../../state';

import styles from './PacingSlider.module.css';

const VOICE_OPTIONS: readonly VoiceName[] = [
  'Aoede',
  'Leda',
  'Kore',
  'Zephyr',
] as const;

export function VoicePicker(): ReactElement {
  const { t } = useTranslation();
  const prefs = usePreferencesStore((s) => s.preferences);
  const setVoiceName = usePreferencesStore((s) => s.setVoiceName);

  const onVoiceChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    void setVoiceName(e.target.value as VoiceName);
  };

  return (
    <section
      className={styles.section}
      aria-labelledby="voice-heading"
      data-testid="voice-picker"
    >
      <h2 id="voice-heading" className={styles.heading}>
        {t('settings.voice.title')}
      </h2>
      <p
        style={{
          margin: '0 0 0.75rem',
          fontSize: '0.85rem',
          color: 'var(--text-secondary, #6b3a55)',
          opacity: 0.85,
        }}
      >
        {t('settings.voice.hint')}
      </p>
      <select
        value={prefs.voiceName}
        onChange={onVoiceChange}
        aria-labelledby="voice-heading"
        style={{
          width: '100%',
          minHeight: '2.75rem',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.75rem',
          border: '1.5px solid rgba(255, 100, 180, 0.25)',
          background: 'rgba(255, 245, 250, 0.5)',
          fontFamily: 'inherit',
          fontSize: '0.9375rem',
          color: 'var(--text-primary, #1a1a1a)',
          cursor: 'pointer',
        }}
      >
        {VOICE_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {t(`settings.voice.${v}`)}
          </option>
        ))}
      </select>
    </section>
  );
}
